import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from "react";
import TimelineColumn from "./features/timeline/TimelineColumn.jsx";
import {
  useTimelineGeometry,
  useTimelineFraming,
} from "./features/timeline/useTimelineGeometry.js";
import { useTimelineMouse } from "./features/timeline/useTimelineMouse.js";
import { useComputeVisibility } from "./features/timeline/useComputeVisibility.js";
import AppFrame from "./features/layout/AppFrame.jsx";
import DeleteConfirmModal from "./features/layout/DeleteConfirmModal.jsx";
import {
  useForecastSelection,
  useForecastSelectionActions,
} from "./features/layout/useForecastSelection.js";
import { useHotkeys } from "./features/layout/useHotkeys.js";
import { useLayoutShell, useRailPanel } from "./features/layout/useLayoutShell.js";
import ChatPanel from "./features/chat/ChatPanel.jsx";
import { useChatComposer } from "./features/chat/useChatComposer.js";
import { useChatWiring } from "./features/chat/useChatWiring.js";
import StaleBundleBanner from "./components/StaleBundleBanner.jsx";
import AccessNoticeStrip from "./features/boot/AccessNoticeStrip.jsx";
import { useBootWiring } from "./features/boot/useBootWiring.js";
import { useUrlHydration } from "./features/boot/useUrlHydration.js";
import BranchControls from "./features/branch/BranchControls.jsx";
import CustomEventPopover from "./features/branch/CustomEventPopover.jsx";
import { useBranchWiring } from "./features/branch/useBranchWiring.js";
import ViewMomentBlock from "./features/reforecast/ViewMomentBlock.jsx";
import { useMarkMoments } from "./features/reforecast/useMarkMoments.js";
import { useReforecastWiring } from "./features/reforecast/useReforecastWiring.js";
import LimitPopup from "./features/tracking/LimitPopup.jsx";
import TrackingBlocks from "./features/tracking/TrackingBlocks.jsx";
import TrackingSettingsOverlay from "./features/tracking/TrackingSettingsOverlay.jsx";
import { useTrackingSettingsWiring } from "./features/tracking/useTrackingSettingsWiring.js";
import { useTrackingWiring } from "./features/tracking/useTrackingWiring.js";
import SignalBar from "./features/signals/SignalBar.jsx";
import SignalLibraryOverlay from "./features/signals/SignalLibraryOverlay.jsx";
import TsdChartSlot from "./features/signals/TsdChartSlot.jsx";
import { useSeriesWiring, useTsdGeometry } from "./features/signals/useSeriesWiring.js";
import PreprocessHost from "./features/submit/PreprocessHost.jsx";
import RunStatusSlot from "./features/submit/RunStatusSlot.jsx";
import SubmitBar from "./features/submit/SubmitBar.jsx";
import { useSubmitEffects } from "./features/submit/useSubmitEffects.js";
import { useSubmitState } from "./features/submit/useSubmitState.js";
import { useSubmitWiring } from "./features/submit/useSubmitWiring.js";
import { renderAuthShell } from "./features/session/AuthShells.jsx";
import { SessionProvider } from "./features/session/SessionProvider.jsx";
import { useSession } from "./hooks/useSession.js";
import { useActiveForecast } from "./hooks/useActiveForecast.js";
import { useHoverTooltip } from "./hooks/useHoverTooltip.js";
import { useForecastAnimation } from "./hooks/useForecastAnimation.js";
import { useChatMessages } from "./hooks/useChatMessages.js";
import { RAIL_W } from "./lib/navRail.js";
import { useQuickLadderPacer } from "./hooks/useQuickLadderPacer.js";
import { useTsdDrawer } from "./hooks/useTsdDrawer.js";
import { useResolverPoll } from "./hooks/useResolverPoll.js";
import { useSeriesPanel } from "./hooks/useSeriesPanel.js";
import { useReforecast } from "./hooks/useReforecast.js";
import { archiveBranch } from "./commands/deleteForecast.js";
import { makeDefaultDeps } from "./commands/deps.js";
import { useSharedAccessNotice } from "./hooks/useSharedAccessNotice.js";
import { useRevokeMyAccess } from "./hooks/useRevokeMyAccess.js";
import { useRealtimeChannel } from "./hooks/useRealtimeChannel.js";
import { useDerivedBranchTriggers } from "./hooks/useDerivedBranchTriggers.js";
import { clearLogRings } from "./lib/logRingBuffer";
import { telemetryLogCounts } from "./services/telemetry.js";
import { emitTierChange } from "./lib/telemetry/uiIntents.js";
import { usePersistedState, JSON_CODEC } from "./hooks/usePersistedState.js";
import { defaultSessionStorage } from "./lib/util/sessionStorage.js";
import { branchCollapseToggled, activeSet, entriesLoaded } from "./lib/forecast/reducer.js";
import {
  useRunStore,
  dispatchRun,
  resetRunStore,
  runStore,
  getRunState,
} from "./store/runStore.js";
import { useArrivals, acknowledgeArrival, resetArrivalsStore } from "./store/arrivalsStore.js";
import { isUnseen } from "./lib/forecast/arrivals.js";
import { PENDING_FORECAST_KEY, ACTIVE_FORECAST_KEY } from "./lib/forecast/storageKeys.js";
import { execTable } from "./store/execTable.js";
import {
  selectLegacyForecasts,
  selectLegacyComparison,
  selectCustomEventPopover,
  selectBranchRuns,
  selectWatchlistForecasts,
  selectSharedForecasts,
  awaitingPreprocess,
} from "./lib/forecast/selectors.js";
import { mergeWatchlistRows, isUserAsked } from "./lib/forecast/forecastLists.js";
import { DRAFT_CONV_ID, loadDraftEntries } from "./lib/forecast/draft.js";
// Trigger marks + legend, the signposts table, and the lane band layout.
// `import.meta.env.DEV` is replaced by the literal `false` at build time, so the DEV guard goes on
// the `lazy()` CALL itself: `false ? lazy(...) : null` makes the dynamic import unreachable and
// Rollup drops the chunk entirely. Guarding only the render site leaves the `lazy(() =>
// import(...))` at module scope, always in the graph, and the page ships in the bundle.
const CardStatesPreview = import.meta.env.DEV
  ? lazy(() => import("./components/dev/CardStatesPreview.jsx"))
  : null;
const ResolutionMatrix = import.meta.env.DEV
  ? lazy(() => import("./components/dev/ResolutionMatrix.jsx"))
  : null;
const SettingsStatesPreview = import.meta.env.DEV
  ? lazy(() => import("./components/dev/SettingsStatesPreview.jsx"))
  : null;
// Sharing is otherwise unreachable locally (it needs a persisted forecast and org claims), so this
// preview uses fabricated shares/candidates.
const ShareDialogPreview = import.meta.env.DEV
  ? lazy(() => import("./components/dev/ShareDialogPreview.jsx"))
  : null;
// A deep run takes up to an hour and costs a paid run, so this replays a real one's stream at speed.
const DeepRunPreview = import.meta.env.DEV
  ? lazy(() => import("./components/dev/DeepRunPreview.jsx"))
  : null;
import GlobalStyles from "./components/GlobalStyles.jsx";
import { Toaster } from "./components/ui/Primitives.jsx";
import { useChatJournal } from "./hooks/useChatJournal.js";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import RedeemCopyRoute from "./components/RedeemCopyRoute.jsx";
import LinkLandingPage from "./components/LinkLandingPage.jsx";
import { matchCopyRoute } from "./lib/copyLinks/route.js";
import { CALIBRATION_MODEL_ID, useReportWiring } from "./features/report/useReportWiring.js";
import { useReportPointerWiring } from "./features/report/useReportPointerWiring.js";
import ReportHost from "./features/report/ReportHost.jsx";
import { useSettingsWiring } from "./features/settings/useSettingsWiring.js";
import SettingsHost from "./features/settings/SettingsHost.jsx";
import TosPreviewHost from "./features/settings/TosPreviewHost.jsx";
import { useOnboardingWiring } from "./features/onboarding/useOnboardingWiring.js";
import TipsHost from "./features/onboarding/TipsHost.jsx";
import { useShareWiring } from "./features/share/useShareWiring.js";
import ShareHost from "./features/share/ShareHost.jsx";
import SendCopyHost from "./features/share/SendCopyHost.jsx";
import { useDebugWiring } from "./features/dev/useDebugWiring.js";
import DebugHost from "./features/dev/DebugHost.jsx";
import { useBugReportWiring } from "./features/bugReport/useBugReportWiring.js";
import BugReportHost from "./features/bugReport/BugReportHost.jsx";
import {
  useTelemetryWiring,
  useTelemetryAppStateWiring,
} from "./features/telemetry/useTelemetryWiring.js";
import { EventModal } from "./components/Overlays.jsx";
import { useQuestionFade } from "./hooks/useQuestionFade.js";
import { resolveClickedRoot } from "./lib/forecast/clickedRoot.js";
import { C } from "./lib/colors.js";
import { tagOwner, effectiveTier, tierSwitchMessage } from "./lib/forecast/engines.js";
import { captureChatTarget } from "./lib/forecast/chatTarget.js";

// While the scripted tour runs, the baked forecast replaces the live server call (deterministic,
// zero backend cost, no cold-start risk). Branch reruns pass a truthy `assumption`, so they get the
// branch variant.
let __tourFixture = null;
function __setTourFixture(f) {
  __tourFixture = f;
}
// Direction of the tour branch the user just created: true if the action should
// RAISE the forecast (a "yes"/blue trigger occurring, or a "no"/red trigger NOT
// occurring), false if it should LOWER it. Set by handleBranch so the baked
// fixture branch reflects what the user actually clicked (red → down, not up).
let __tourBranchIncrease = true;
// Lets useBranchForecast write this module global from its own module; tour globals are threaded
// via setters, never copied.
function __setTourBranchIncrease(v) {
  __tourBranchIncrease = v;
}

// Module-level mirror of /api/me's authEnabled flag, read live by the tour (a branch only
// stamps a server forecast id when there is a signed-in user and a database to hold it).
let __authEnabled = true;
function setAuthEnabledFlag(v) {
  __authEnabled = !!v;
}

// Per-tab handle (sessionStorage, not localStorage) so a fresh login lands on the blank/home state
// while a reload within the same tab restores the last-viewed forecast. The sessionStorage scope
// also lets in-flight branch reattaches find their root forecast as `active`, so the placeholder
// branch stays visible during resume.

function AppInner() {
  // AppInner's first hook reads the session `SessionProvider` (App, below) provides; the wirings
  // read the same context themselves.
  const session = useSession();
  const {
    me,
    preferences,
    setPreferences,
    authEnabled,
    persistenceEnabled,
    _forecastTrackingAllowed,
    quickDeepSelectorAllowed,
    shiftVerdictEnabled,
    allMultiwayEnabled,
    forecastLiveProgressEnabled,
    gcpProject,
    alternateSpacesEnabled,
    reportListenEnabled,
    dynamicReportEnabled,
    tos,
    setTos,
    setTosPreview,
    authLoading,
  } = session;
  // Last-observed observability metadata, surfaced in the user-panel debug section. `lastObs` holds
  // the last EXECUTED forecast's trace id, backend and request id, populated from the
  // x-forecaster-trace-id / x-forecast-backend / x-request-id response headers and the bug-report
  // POST response. It drives sooth-sql /trace lookups: copy the trace id, switch to staging, run
  // /trace <id> to see the full call chain. Click-loads and switches never call setLastObs, so they
  // never overwrite it.
  const [lastObs, setLastObs] = usePersistedState(
    "sooth-lastObs",
    null,
    JSON_CODEC,
    defaultSessionStorage,
  );
  // The identity selector re-renders on every store change; `dispatchRun` is the imported, stable
  // store dispatch.
  const runState = useRunStore((s) => s);
  // The run-store is a module-level singleton, so its state would otherwise leak
  // across mounts (StrictMode remount, Fast Refresh, future test mounts). Reset it
  // on unmount so each fresh mount of AppInner starts from initialState.
  useEffect(() => () => resetRunStore(), []);
  useEffect(() => () => resetArrivalsStore(), []);
  // The pendingId of the most recent TOUR submission, stashed synchronously by
  // submit() (the useReducer dispatch that sets activeRunId only lands on the next
  // render, so startTour can't read activeRunId right after submit — it reads this
  // ref instead to eager-create the example forecast row during generation).
  const lastTourPendingIdRef = useRef(null);
  // The runId of the tour's example forecast for the CURRENT tour session. isTourExample lives
  // only on the in-session run object — it is NOT written to the wire, so a run HYDRATED from the
  // DB on reload-resume loses it, and pushSayerMsg's persistence gate would then drop all
  // post-resume narration (legend + later steps). This ref re-asserts "this run is the tour
  // example" across reloads so the gate keeps persisting.
  const tourExampleRunIdRef = useRef(null);
  // Points at the store/execTable module singleton, so every hook/adapter shares the SAME instance
  // the commands import directly.
  const execByRunIdRef = useRef(null);
  if (execByRunIdRef.current === null) execByRunIdRef.current = execTable;
  // Ahead of the `forecasts` memo below, which reads `isWatchlisted`.
  const trackingWiring = useTrackingWiring();
  const { pinned, setPinned, pinnedRef, isWatchlisted } = trackingWiring;
  // `forecasts` is the capped display list UNION the user's watchlisted rows taken from the full
  // store. Without the union, a tracked forecast older than FORECAST_DISPLAY_CAP is absent from
  // every list derived from here, so it vanishes from WATCHLIST while the server still tracks and
  // reforecasts it. The extras stay far smaller than the full list (see selectWatchlistForecasts on
  // why WATCHLIST_LIMIT is a soft, not hard, bound), so this stays cheap, and merging at the source
  // means the ordering, time-travel overlay, and forecastsById lookup downstream all see them
  // without further plumbing.
  //
  // Composed with isUserAsked so the watchlist scan doesn't map a minted trigger row through the
  // expensive _runToLegacyForecast just to have the same filter drop it at render. Minted rows
  // inside the display window are still mapped by selectLegacyForecasts.
  const forecasts = useMemo(() => {
    const state = runState;
    return mergeWatchlistRows(
      selectLegacyForecasts(state, forecastLiveProgressEnabled),
      selectWatchlistForecasts(
        state,
        (f) => isUserAsked(f) && isWatchlisted(f),
        forecastLiveProgressEnabled,
      ),
    );
  }, [runState, isWatchlisted, forecastLiveProgressEnabled]);
  const active = useActiveForecast();
  // Declared HERE, before the snapshot-rewind block and its effects, which reference it during
  // render, so the binding exists (no TDZ).
  //
  // The single gate is `_forecastTrackingAllowed`, the deployment bit from the server-side
  // `chatboard-forecast-tracking` GOFF flag surfaced via /api/me (ON locally via
  // `FF_CHATBOARD_FORECAST_TRACKING` in start_debug.sh; fail-safe OFF with no relay and no
  // override).
  const forecastTracking = _forecastTrackingAllowed;
  // The resubmit (useReforecast, below) stays after this point: its latest input is set there.
  const reforecastWiring = useReforecastWiring();
  const {
    reforecastEntries,
    viewMomentIso,
    viewEntry,
    viewSnap,
    currentResolutionEntry,
    displayActive,
  } = reforecastWiring;

  // ── COPY LINKS ARE ON HOLD (collaborative-forecasts design) ───────────────
  // The design replaces the copy verb with revocable collaboration built on the
  // share ACL, and holds copy until it has a job that is not "the only way a
  // recipient can interact". So the owner-side copy controls do not render, and
  // SHARE is the single entry point.
  //
  // Held HERE, at the consumer, deliberately: not by deleting the panel (every
  // hook, component and test behind it stays wired and covered, so bringing it
  // back is one constant rather than a rebuild), not inside `useCopyLinksGate`
  // (that would short-circuit the gate's own AND and take its tests with it), and
  // not by flipping `chatboard-copy-links` in cloud/feature-flags (a
  // CODEOWNER-owned path, which would gate an otherwise self-mergeable change on
  // admin review).
  //
  // The `/copy/:id` REDEEM route is deliberately NOT affected — a link someone was
  // already sent must keep working, and the server enforces its own gate there.
  const COPY_LINKS_UI_ON_HOLD = true;
  // Read by features/share/useShareWiring.js (called after `useReforecast`, below), which owns the
  // gates this constant holds shut.
  // Hydrate the draft conversation from sessionStorage on mount
  // so pre-forecast introduction chat survives a page reload within the same tab.
  // Runs once ([] deps) — a later adoption call replaces the draft with the real
  // conversation, and clearDraft() prevents it from re-appearing on a second load.
  useEffect(() => {
    const saved = loadDraftEntries();
    if (saved.length > 0) dispatchRun(entriesLoaded(DRAFT_CONV_ID, saved));
  }, []);
  const { series, setSeries, fetchLiveSeries, verifySeries, addSeries, removeSeries } =
    useSeriesPanel({ active });
  const [contractPopupOpen, setContractPopupOpen] = useState(false);
  const [contractBlink, setContractBlink] = useState(false);
  const submitState = useSubmitState();
  const {
    loading,
    setLoading,
    forecastAbortRef,
    pendingPreprocess,
    setPendingPreprocess,
    setForecastingPhase,
    activeTrace,
  } = submitState;
  // All in-flight branch / chat-classification AbortControllers: useBranchForecast (row 7), the
  // tour (row 12) and the chat's answerChat / useChatIntent (features/chat/) add theirs; the
  // chat's force-submit paths and the tour's endTour abort the lot (resetForecast / CLEAR does
  // NOT — a detached run keeps streaming). AppInner's, not any one bucket's — three read it.
  const pendingAbortsRef = useRef(new Set());
  // Cleared on reset so the examples can't pop back in after a CLEAR.
  const exampleRevealTimerRef = useRef(null);
  // 45s nudge timer armed when the post-tour "Replay this tour…" tip shows. Fires
  // a "submit your own question" nudge unless the user submits something first
  // (cleared in submit()).
  const postTourNudgeTimerRef = useRef(null);
  // Tour-only, like ninetyNotePendingRef (features/onboarding/useOnboardingWiring.js): set when
  // the tour prompts the first forecast; submit shows "Next, a forecast contract will be created…".
  const contractNotePendingRef = useRef(false);
  // When the chat asks to use a different engine "just for this question", we save the
  // previous engine here so doForecast can restore it after the run finishes.
  const pendingEngineRevertRef = useRef(null);
  // One-shot: after that first post-tour forecast generates, run the timed tip
  // sequence (replay hint, then the bug/feature note). postTourTipRunRef holds
  // the sequence fn (set in the tour controller below) so doForecast can trigger
  // it without a dependency cycle. Declared here, with the other refs the
  // useForecastSubmit hook reads, so the hook call can receive them.
  const postTourTipRef = useRef(false);
  const postTourTipRunRef = useRef(null);
  // One-shot: when the tour ends inviting the user to "ask your own question",
  // the next thing they type should run a forecast directly — not get routed
  // through the LLM classifier (which would ask "do you want a new forecast?").
  const forecastNextRef = useRef(false);
  // Holds a forecasting question the classifier is ASKING the user to confirm as
  // a NEW forecast ("run a fresh forecast on X, or ask about the current one?").
  // Set when classifyAndDispatch returns intent `confirm_new_forecast`; the NEXT
  // chat message is then resolved DETERMINISTICALLY in handleChatMessage (yes →
  // submit this question; no → drop it) rather than round-tripping the reply back
  // through the LLM, which fumbled short confirmations like "new one". Null = no
  // pending confirmation.
  const pendingNewForecastRef = useRef(null);
  // Owner-scoped cache of slide anchors for per-event label placement.
  const slideCache = useRef({});
  // Forward ref to useSayerTour's resumeTour(): commands/bootForecasts.ts's restore
  // decision (invoked from useBootForecasts, called before useSayerTour exists this
  // render) reads it on a 'tour' restore decision. resumeTour is only ever called
  // post-mount (from an effect), so the ref is always set by then.
  const resumeTourRef = useRef(null);
  // Late-bound bridge to pushSayerMsg (defined far below, after useChatMessages)
  // so useBugReport's submitBug — which runs at click time, long after mount —
  // can append the submit confirmation/failure line to the active chat. Same commit-synced
  // forward-ref pattern as resumeTourRef (a useLayoutEffect writes it after each commit).
  const pushSayerMsgRef = useRef(null);
  // The selected forecast's custom-event popover, from its run's slot in the store
  // (reducer.ts CustomEventPopover): {screenX, screenY, date, dateTime, description?, problem?}.
  // `date` is the DAY the picker shows, `dateTime` the full INSTANT the user double-clicked at.
  // Both, because the picker is an `<input type="date">` and cannot hold a time, while the
  // node's position and its isPast/mintability need one (see resolveTriggerInstant). Another
  // forecast's popover stays on that forecast and shows again when it is selected.
  const customEvtPopoverRunId = captureChatTarget(runState).runId;
  const customEvtPopover = selectCustomEventPopover(runState, customEvtPopoverRunId);
  const {
    hov,
    setHov,
    hoveredBranch,
    setHoveredBranch,
    hoveredBranchTrigger,
    setHoveredBranchTrigger,
    hoveredBin,
    setHoveredBin,
    hovPos,
    setHovPos,
    citCard,
    setCitCard,
    hovEvtRef,
    longPressRef,
  } = useHoverTooltip();
  // Spine hover is per-lane local state inside <ForecastLane>, so it brightens only the hovered
  // lane's dashes.
  const [chatThinking, setChatThinking] = useState(false);
  const {
    tsDrawerOpen,
    setTsDrawerOpen,
    tsdAnimating,
    tsdH,
    setTsdH,
    tsdDragRef,
    setTsdIsDragging,
    onTsdHandleMD,
  } = useTsdDrawer();
  // After useTsdDrawer (the drawer z-order reads `tsDrawerOpen`), before useTelemetryWiring, whose
  // panel-toggle intent reads the rail's sections.
  const layoutShell = useLayoutShell({ contractPopupOpen, setContractPopupOpen, tsDrawerOpen });
  const {
    logOpen,
    chatOpen,
    contractOpen,
    setLogOpen,
    setChatOpen,
    setContractOpen,
    railSection,
    toggleRailCollapse,
    forecastHeaderH,
    railTimelineDrop,
  } = layoutShell;
  const forecastSelection = useForecastSelection({
    preferences,
    setPreferences,
    forecasts,
    active,
    viewSnap,
    viewMomentIso,
    pinnedRef,
  });
  const {
    removeFromForecastOrder,
    sharedForecastsRef,
    forecastStacking,
    moveForecastToTop,
    selectForecastRef,
    deleteConfirm,
    setDeleteConfirm,
    setDetailLevel,
    compareMode,
    setCompareMode,
  } = forecastSelection;
  const redBlueTeam = true;
  // Dual-Engine comparison has no dedicated flag: it is gated only by the Dual-Engine checkbox
  // (compareMode state), which renders only inside the debug UI (sooth-debugUI).
  // Hoisted above `forecastsById` (it needs only `runState`) so that lookup, which avoids an
  // O(pins×forecasts) scan per watchlist row render in getTrackingPrefs, can carry the shared
  // lane too — see its note.
  const sharedForecasts = useMemo(
    () => selectSharedForecasts(runState, forecastLiveProgressEnabled),
    [runState, forecastLiveProgressEnabled],
  );
  // Kept current after every commit; useForecastSelection's drag/move callbacks read it.
  useLayoutEffect(() => {
    sharedForecastsRef.current = sharedForecasts;
  });
  // OWNED **AND** SHARED. `toggleWatchlist` and `applySchedule` resolve their row here
  // and return early on a miss, so a shared row absent from this map made those writes
  // no-ops while the optimistic update still ran. Owner leg last, so it wins on a
  // duplicate id — the same preference the settings-panel lookup makes.
  const forecastsById = useMemo(() => {
    const m = new Map();
    for (const f of sharedForecasts) if (f) m.set(f.id, f);
    for (const f of forecasts) if (f) m.set(f.id, f);
    return m;
  }, [forecasts, sharedForecasts]);
  // `noticeShown` is declared below: the arrow defers the read to write time, so the arrow is the
  // input, never `noticeShown`.
  const trackingSettingsWiring = useTrackingSettingsWiring({
    forecastsById,
    pinnedRef,
    setPinned,
    onSharedAccessLost: (_id, message) => noticeShown(message),
  });
  const { applySchedule, setTrackingSettingsId } = trackingSettingsWiring;
  const {
    debugMode,
    setDebugMode,
    mockToday,
    setMockToday,
    debugResolutionOverride,
    setDebugResolutionOverride,
    debugPremiseOverride,
    setDebugPremiseOverride,
    debugForceGenerating,
    setDebugForceGenerating,
    clearDebugOverrides,
    debugForceAlertBadge,
    setDebugForceAlertBadge,
    debugPanelOpen,
    setDebugPanelOpen,
    gcpResponse,
    setGcpResponse,
    gcpLoading,
    setGcpLoading,
    llmPrompt,
    setLlmPrompt,
    llmModel,
    setLlmModel,
    llmResponse,
    setLlmResponse,
    llmLoading,
    setLlmLoading,
  } = useDebugWiring();
  // Comparison is keyed on server forecastId; rootIds[0] is the focused lane.
  const comparison = useMemo(() => selectLegacyComparison(runState), [runState]);
  const { animPhase, setAnimPhase, branchAnim, setBranchAnim, startForecastAnim, startBranchAnim } =
    useForecastAnimation();
  const reportWiring = useReportWiring({
    displayActive,
    comparison,
  });
  const { showReport, openReport, closeReportRef } = reportWiring;
  const { closeReportRef: _closeReportRef, ...reportWiringCtx } = reportWiring;
  // Before useSeriesWiring, whose auto-plot picks the theme's slot colors (useTheme is in here).
  const settingsWiring = useSettingsWiring({
    viewSnap,
    viewEntry,
    runState,
  });
  const {
    alternateSpaceDefaultPref,
    setAlternateSpaceDefaultPref,
    alternateSpaceDefault,
    applyAlternateSpace,
    debugUI,
    setDebugUI,
    responseCaptureOptIn,
    setResponseCaptureOptIn,
    settingsTitleClickRef,
    proOpen,
    setProOpen,
    accountOpen,
    setAccountOpen,
    proColors,
    preprocessVariantAvailable,
    preprocessVariantId,
    setPreprocessVariantId,
    preprocessVariants,
    preprocessVariantDeploymentDefault,
    preprocessVariantsLoading,
    glowSpine,
    setGlowSpine,
    glowControls,
    setGlowControls,
    compareEngine,
    setCompareEngine,
    forecastEngine,
    setForecastEngine,
    mirabilisLanes,
    setMirabilisLanes,
    orchestrators,
    setOrchestrators,
    triggerLlm,
    setTriggerLlm,
    compareTriggerLlm,
    setCompareTriggerLlm,
    theme,
    setTheme,
    uiScaleSetting,
    setUiScale,
    uiScale,
    isPhone,
    uiZoom,
    themeRaw,
    voiceMode,
    autoRead,
    setAutoRead,
  } = settingsWiring;
  const { settingsTitleClickRef: _settingsTitleClickRef, ...settingsFrameCtx } = settingsWiring;
  // After useSettingsWiring (useTheme is inside it): the auto-plot picks the theme's slot colors;
  // useSeriesPanel's outputs above are this hook's inputs.
  const seriesWiring = useSeriesWiring({
    series,
    setSeries,
    fetchLiveSeries,
    addSeries,
    removeSeries,
    dispatchRun,
    theme,
  });
  const { clearSeriesSelection } = seriesWiring;
  // The ownership handle is the hook's own (claimed by loadForecastSeries, pinned by its stem
  // test); it never reaches the frame context — frameCtxShape.test.js forbids it there.
  const { seriesOwnerRef: _seriesOwnerRef, ...seriesCtx } = seriesWiring;
  const { bugOpen, setBugOpen, openBugReport, lastBugObs, bugReportCtx } = useBugReportWiring({
    activeTrace,
    lastObs,
    pushSayerMsgRef,
  });
  const timeline = useTimelineGeometry({
    active,
    allMultiwayEnabled,
    applyAlternateSpace,
    compareMode,
    comparison,
    currentResolutionEntry,
    debugPremiseOverride,
    debugResolutionOverride,
    displayActive,
    forecastHeaderH,
    forecastLiveProgressEnabled,
    forecastTracking,
    forecasts,
    mockToday,
    railTimelineDrop,
    runState,
    slideCache,
    themeRaw,
    viewEntry,
    viewMomentIso,
    viewSnap,
  });
  const {
    animRef,
    autoZoomRef,
    axisHoverX,
    branches,
    cSize,
    cSizeRef,
    compareOn,
    compareRoots,
    effectiveToday,
    eventAnchorMs,
    evtScrollRef,
    measured,
    panAnimRef,
    ppd,
    ppdRef,
    scheduleRender,
    selEvt,
    selRect,
    setSelEvt,
    timelineToday,
    todayDay,
    todayX,
    vPanAnimRef,
    viewLeft,
    vlRef,
  } = timeline;

  // After the five overlay bindings its focus effect reads; `input` is first read by the submit
  // wiring below.
  const chatComposer = useChatComposer({
    chatOpen,
    selEvt,
    bugOpen,
    pendingPreprocess,
    showReport,
    customEvtPopover,
  });
  const { input, setInput } = chatComposer;

  // Invariant: in compare mode the primary (top) lane spine renders `active`, and
  // the lead lane is compareRoots[0]. So active MUST equal compareRoots[0]. The
  // shift+click seed maintains this, but hydrating a ?compare= URL sets the
  // selection WITHOUT setting active — leaving the top spine showing whatever
  // forecast was active (often the OTHER selected root), so both lanes rendered
  // the same forecast and the lead root vanished. Pin active to the lead whenever
  // they diverge. Safe: no effect auto-loads chat on active change (compare chat
  // is merged separately via the hydration's mergeThreads), so this only refreshes
  // the derived spine/branch render — exactly what we want.
  useEffect(() => {
    if (!compareOn) return;
    const lead = compareRoots[0];
    if (!lead || lead.id === active?.id) return;
    // Don't clobber a fresh in-flight submission. If `active` is a running
    // _pending placeholder that ISN'T part of the current compare pair, the user
    // has started a new forecast while a stale comparison selection still
    // resolves (compareMode left on); pinning active back to the old lead would
    // wipe the new placeholder's spine. Leave it; the new run re-seeds selection.
    if (
      active?._pending &&
      !compareRoots.some(
        (r) => r.id === active.id || (r.forecastId != null && r.forecastId === active.forecastId),
      )
    )
      return;
    dispatchRun(activeSet(lead.id));
  }, [compareOn, compareRoots, active?.id, active?._pending]);
  // Before useTelemetryAppStateWiring: a focus change re-stamps the ring's active_* ids before
  // forecast.open begins — pinned by features/telemetry/__tests__/useTelemetryWiring.test.jsx.
  useTelemetryWiring({ activeTrace, logOpen, chatOpen, tsDrawerOpen, contractOpen });

  // After the timeline geometry, before useRealtimeChannel, which reads `forecastsLoaded`.
  const bootWiring = useBootWiring({
    forecasts,
    pinned,
    setPinned,
    autoZoomRef,
    setForecastingPhase,
    setPendingPreprocess,
    setLastObs,
    setCompareMode,
    debugUI,
    selectForecastRef,
    resumeTourRef,
    runStore,
    measured,
    cSize,
    todayDay,
    scheduleRender,
    vlRef,
    ppdRef,
  });
  const {
    bootPhase,
    forecastsLoaded,
    pendingRestore,
    loadConversationThread,
    reattachToForecast,
    loadedRef,
    claimLoadSlot,
    isCurrentLoadSlot,
    sharedSettled,
    linkClaim,
  } = bootWiring;
  const { loadedRef: _loadedRef, ...bootCtx } = bootWiring;

  // The one transient notice: lost access (rule: sharedAccessLoss.js) and the deep-link notices.
  const { accessNotice, noticeShown } = useSharedAccessNotice();

  // The recipient's own "revoke my access". It reuses the same notice, with its
  // own wording: the row leaving is expected here rather than a discovery, so
  // saying "no longer shared with you" would misdescribe what just happened.
  const { revokeMyAccess } = useRevokeMyAccess({
    onDone: useCallback(() => {
      setTrackingSettingsId(null); // the panel was the entry point
      noticeShown("You no longer have access to that forecast.");
    }, [noticeShown, setTrackingSettingsId]),
  });

  // The pending-delete queue lives in the run store: deleteForecast/deleteBranch dispatch to it.
  const mkDeleteDeps = useCallback(
    () => ({ ...makeDefaultDeps(), ui: { setPinned } }),
    [setPinned],
  );

  // Opens the realtime SSE channel while signed in, so auto-reforecasts and resolutions land in
  // the live view without a reload (every hint → reconcile([qkey]), owner and shared rows alike).
  // Gated on a signed-in session AND `forecastsLoaded`: only authed users hold forecasts the
  // channel filters on, and waiting for load avoids a race where a hint arriving before
  // /api/forecasts populates the store no-ops against an empty run store (the on-load overlay
  // already promotes the newest state at hydration, so the channel only needs to carry live
  // updates from there on).
  useRealtimeChannel({ enabled: authEnabled && !!me && forecastsLoaded });
  // Paces live quick-tier runs from Researching to Weighing (the server never signals it).
  useQuickLadderPacer();
  // A derived branch lands with no triggers; generate its own once and attach them.
  useDerivedBranchTriggers({
    enabled: authEnabled && !!me && forecastsLoaded,
    onAttached: (runId, n) => startBranchAnim(runId, Math.max(1, n), { keepResult: true }),
  });

  // Forward ref to the log rows' handleSelect (ForecastManagerOverlay assigns it during render), so
  // the one-shot ?forecast= deep-link effect (features/boot/useUrlHydration.js, called below) can
  // focus a forecast the same way a row click does.
  const handleSelectRef = useRef(null);
  const forecastSelectionActions = useForecastSelectionActions({
    dispatchRun,
    reattachToForecast,
    selectForecastRef,
    handleSelectRef,
    isPhone,
    forecastTracking,
    mkDeleteDeps,
  });

  // Persist the currently-active forecast id across reloads so a refresh
  // restores the same forecast instead of dumping the user on the home
  // screen. Scoped to sessionStorage (per-tab) so a fresh login on a new
  // tab still boots blank — see ACTIVE_FORECAST_KEY definition. Skips the
  // first run so the initial active=null at mount doesn't clobber the
  // key the previous tab session wrote — without that guard, the write
  // effect (declared above the restore effect) fires first on mount and
  // wipes the key before the restore effect can read it.
  const activeWriteFirstRunRef = useRef(true);
  useEffect(() => {
    if (activeWriteFirstRunRef.current) {
      activeWriteFirstRunRef.current = false;
      return;
    }
    // Persist the active forecast as the reload-restore target when it's either
    // SETTLED (completed/errored/cancelled) or RUNNING-with-a-traceId (i.e.
    // reattachable). The restore effect resumes a running target via
    // selectForecast({reattach:true}) — so remembering it restores the live run
    // instead of dropping back to an older forecast. Only skip the unreattachable
    // in-flight state: a pre-traceAllocated placeholder (no traceId), which the
    // PENDING_FORECAST_KEY resume path covers instead.
    try {
      if (active && active.id && (!active._pending || active.traceId)) {
        sessionStorage.setItem(ACTIVE_FORECAST_KEY, active.id);
      } else if (!active) {
        sessionStorage.removeItem(ACTIVE_FORECAST_KEY);
      }
    } catch (_e) {
      /* private mode / quota — skip */
    }
  }, [active]);
  const railPanel = useRailPanel({
    forecastTracking,
    railSection,
    vlRef,
    ppdRef,
    scheduleRender,
    uiZoom,
  });
  const { railPanelWidth, railReframeRef, railPanelOpen, railPanelSettled } = railPanel;
  const timelineFraming = useTimelineFraming({
    geom: timeline,
    active,
    allMultiwayEnabled,
    applyAlternateSpace,
    authLoading,
    chatOpen,
    logOpen,
    railPanelOpen,
    railPanelWidth,
    runState,
  });
  const { autoZoom, centerToday, fitTimeline, jumpTo } = timelineFraming;

  const reportPointerWiring = useReportPointerWiring({
    showReport,
    effectiveToday,
    autoZoom,
    vlRef,
    ppdRef,
    cSizeRef,
    animRef,
    scheduleRender,
    railPanelOpen,
    toggleRailCollapse,
  });
  const { reportListen, reportRowKey, forecastPointerCount, asyncMoments } = reportPointerWiring;
  // Kept current after every commit; useNavRail calls it from its passive [reframeToken] effect.
  useLayoutEffect(() => {
    railReframeRef.current = centerToday;
  });
  const timelineMouse = useTimelineMouse({ geom: timeline, setTsdH, setTsdIsDragging, tsdDragRef });
  const { onML, onMM, onMU } = timelineMouse;
  const { newQuestionRef } = useHotkeys({
    jumpTo,
    ppdRef,
    panAnimRef,
    vlRef,
    scheduleRender,
    evtScrollRef,
    vPanAnimRef,
    setTsDrawerOpen,
    tsdH,
    setTsdH,
    setLogOpen,
    setChatOpen,
  });

  const branchWiring = useBranchWiring({
    compareRoots,
    forecastEngine,
    series,
    setLoading,
    setPendingPreprocess,
    pendingAbortsRef,
    slideCache,
    eventAnchorMs,
    startBranchAnim,
    setLastObs,
    getTourFixture: () => __tourFixture,
    getAuthEnabled: () => __authEnabled,
    getTourBranchIncrease: () => __tourBranchIncrease,
    setTourBranchIncrease: (v) => __setTourBranchIncrease(v),
    mockToday,
    customEvtPopoverRunId,
    customEvtPopover,
    getRunState,
    dispatchRun,
  });
  const { handleBranch, prefetchDerivedBranch, chatCustomEventBranch } = branchWiring;

  // CLEAR BRANCH archives (commands/deleteForecast.archiveBranch): the branch leaves the live
  // lane but a past mark from before the clear still draws it.
  const removeBranch = useCallback(
    (branchId) => void archiveBranch(mkDeleteDeps(), { branchId }),
    [mkDeleteDeps],
  );

  const toggleBranchCollapsed = useCallback((branchId) => {
    dispatchRun(branchCollapseToggled(branchId));
  }, []);

  const allEngines = orchestrators;

  const blinkContract = useCallback(() => {
    setContractBlink(true);
    setTimeout(() => setContractBlink(false), 2500);
  }, []);

  // Must come before its consumers (dispatchIntent / handleChatMessage / the tour controller list
  // `submit` in their dep arrays), so `submit`/`doForecast` are defined before those bindings
  // evaluate. The tour globals are passed as live getter thunks read at call time: the hook lives
  // in a separate module and can't see App.jsx's module scope, and capturing their values here
  // would be stale (the tour sets __tourFixture then calls submit).
  const getTourFixture = () => __tourFixture;
  const getAuthEnabled = () => __authEnabled;
  const getTourBranchIncrease = () => __tourBranchIncrease;
  const setTourFixture = (f) => __setTourFixture(f);
  // setTourExamples is the tour controller's, declared below: it reaches this hook through the
  // forward ref.
  const setTourExamplesRef = useRef(null);
  const submitWiring = useSubmitWiring({
    runState,
    dispatchRun,
    input,
    setInput,
    ...submitState,
    exampleRevealTimerRef,
    postTourNudgeTimerRef,
    setTourExamples: (v) => setTourExamplesRef.current(v),
    setChatThinking,
    setDebugPanelOpen,
    setContractOpen,
    setAnimPhase,
    setBranchAnim,
    setTrackingSettingsId,
    forecastEngine,
    compareMode,
    compareEngine,
    triggerLlm,
    compareTriggerLlm,
    debugMode,
    series,
    preprocessVariantId,
    setLastObs,
    setForecastEngine,
    slideCache,
    pushSayerMsgRef,
    contractNotePendingRef,
    lastTourPendingIdRef,
    postTourTipRef,
    postTourTipRunRef,
    pendingEngineRevertRef,
    autoZoom,
    blinkContract,
    startForecastAnim,
    fetchLiveSeries,
    clearSeriesSelection,
    reattachToForecast,
    getTourFixture,
    getTourBranchIncrease,
  });
  const { submit, resetForecast } = submitWiring;

  const { startResolverPoll } = useResolverPoll({
    forecastTracking,
    mockToday,
  });

  // Reforecast resubmits the active forecast's question through the same orchestrator and merges
  // the new result onto the old, preserving every event whose date is in the past (so the user sees
  // the actual realized timeline); future events come from the new response. Each reforecast pushes
  // the previous (contract, events, report, server context) onto `active.history` so the report
  // modal can scroll through every snapshot.
  const { reforecast } = useReforecast({
    forecastTracking,
    forecastLiveProgressEnabled,
    active,
    loading,
    forecastEngine,
    mockToday,
    startResolverPoll,
    setLoading,
    setForecastingPhase,
    setLastObs,
    getTourFixture,
    getTourBranchIncrease,
    slideCache,
  });
  const shareWiring = useShareWiring({
    COPY_LINKS_UI_ON_HOLD,
    reforecast,
    applySchedule,
    forecastsById,
  });

  const { pushUserMsg, pushSayerMsg, appendEntry } = useChatMessages({
    tourExampleRunIdRef,
  });
  // Kept current after every commit: useBugReport's reply and doForecast's landing/failure messages read `.current` after their awaits.
  useLayoutEffect(() => {
    pushSayerMsgRef.current = pushSayerMsg;
  });
  // The Quick/Deep pill shares `forecastEngine` with the Settings segmented control, so the two
  // stay in sync both ways with no extra wiring. On a real swap, echo the tier's own switch line
  // into chat so Sayer explains what the mode is.
  const handleSelectTier = useCallback(
    (tag, at) => {
      const owner = tagOwner(tag);
      if (!owner || effectiveTier(forecastEngine) === tag) return;
      emitTierChange(tag, at);
      setForecastEngine(owner);
      pushSayerMsg(tierSwitchMessage(tag), { target: captureChatTarget(getRunState()) });
    },
    [forecastEngine, setForecastEngine, pushSayerMsg],
  );

  // After useChatMessages (it needs `submit` and `pushSayerMsg`); useChatWiring and
  // useSubmitEffects below take its outputs.
  const onboardingWiring = useOnboardingWiring({
    submit,
    pushSayerMsg,
    resetForecast,
    setInput,
    setChatOpen,
    autoZoom,
    openReport,
    closeReportRef,
    forecastAbortRef,
    pendingAbortsRef,
    setChatThinking,
    setLoading,
    setForecastingPhase,
    contractNotePendingRef,
    lastTourPendingIdRef,
    postTourNudgeTimerRef,
    postTourTipRef,
    postTourTipRunRef,
    forecastNextRef,
    exampleRevealTimerRef,
    tourExampleRunIdRef,
    branches,
    hov,
    logOpen,
    contractOpen,
    chatOpen,
    showReport,
    tsDrawerOpen,
    proOpen,
    accountOpen,
    isPhone,
    forecastsLoaded,
    getAuthEnabled,
    setTourFixture,
    setTourExamplesRef,
    resumeTourRef,
  });
  const {
    tourActive,
    setTourExamples,
    tourRef,
    ninetyNotePendingRef,
    tourEndConfirmRef,
    drawerHelpState,
    setDrawerHelpState,
    resetTips,
    howSayerWorks,
  } = onboardingWiring;
  const {
    tourRef: _tourRef,
    ninetyNotePendingRef: _ninetyNotePendingRef,
    tourEndConfirmRef: _tourEndConfirmRef,
    ...onboardingCtx
  } = onboardingWiring;

  // One continuous chat across forecasts, kept per user in this browser (display only).
  const chatJournal = useChatJournal({ me, authEnabled, authLoading, active, handleSelectRef });
  // New Question, from the rail and from Shift+↑.
  const startNewQuestion = () => {
    chatJournal.newQuestion();
    resetForecast();
    setTsDrawerOpen(false);
    setContractOpen(false);
  };
  // Kept current after every commit; useHotkeys reads it from its keydown listener (Shift+↑).
  useLayoutEffect(() => {
    newQuestionRef.current = startNewQuestion;
  });
  const { earlierConversation } = chatJournal;

  // Before useTelemetryAppStateWiring, which takes the hydrated refs.
  const urlHydration = useUrlHydration({
    forecastsLoaded,
    sharedSettled,
    linkClaim,
    loadedRef,
    claimLoadSlot,
    isCurrentLoadSlot,
    loadConversationThread,
    forecasts,
    forecastLiveProgressEnabled,
    dispatchRun,
    debugUI,
    setCompareMode,
    autoZoom,
    pushSayerMsg,
    handleSelectRef,
    noticeShown,
    runStore,
  });
  const { compareHydratedRef, forecastDeepLinkRef } = urlHydration;
  // After useTelemetryWiring (pinned by features/telemetry/__tests__/useTelemetryWiring.test.jsx)
  // and after useUrlHydration, whose hydrated refs it takes.
  useTelemetryAppStateWiring({
    forecastsLoaded,
    sharedSettled,
    compareHydratedRef,
    forecastDeepLinkRef,
    forecasts,
    sharedForecasts,
    forecastEngine,
    mockToday,
    debugResolutionOverride,
    debugPremiseOverride,
    debugForceGenerating,
  });

  // After the tour controller (it takes `tourRef`), before useSubmitEffects, which takes
  // `handleChatMessage`.
  const chatWiring = useChatWiring({
    setLogOpen,
    openBugReport,
    setProOpen,
    setAccountOpen,
    setTsDrawerOpen,
    setBugOpen,
    getRunState,
    setChatThinking,
    pendingAbortsRef,
    pushSayerMsg,
    submit,
    earlierConversation,
    howSayerWorks,
    series,
    input,
    forecastEngine,
    allEngines,
    handleBranch,
    chatCustomEventBranch,
    addSeries,
    removeSeries,
    jumpTo,
    centerToday,
    openReport,
    resetForecast,
    setDetailLevel,
    setForecastEngine,
    appendEntry,
    fitTimeline,
    setInput,
    setTourExamples,
    setTourFixture,
    pushUserMsg,
    pendingEngineRevertRef,
    pendingNewForecastRef,
    tourRef,
    tourEndConfirmRef,
    exampleRevealTimerRef,
    forecastNextRef,
    postTourTipRef,
    ninetyNotePendingRef,
    contractNotePendingRef,
  });
  const { handleChatMessage } = chatWiring;
  // After useChatWiring: it takes `handleChatMessage`.
  const submitEffects = useSubmitEffects({
    active,
    runState,
    getRunState,
    handleSelectRef,
    setTrackingSettingsId,
    moveForecastToTop,
    handleChatMessage,
    forecastEngine,
    ...submitState,
    ninetyNotePendingRef,
    pushSayerMsg,
    input,
    selEvt,
    bugOpen,
    showReport,
    customEvtPopover,
    isPhone,
    compareOn,
    forecastLiveProgressEnabled,
    bootPhase,
    pendingRestore,
    tourActive,
  });

  // The title stays empty until preprocess has refined the question (never the raw typed text).
  // Then: the contract question, the run's own question, contract.title for older rows that
  // predate a stored question, and GENERATING while a run is in flight.
  const titlePreprocessing = awaitingPreprocess(active);
  const displayTitle = titlePreprocessing
    ? null
    : active?.contract?.question ||
      active?.question ||
      active?.contract?.title ||
      (loading ? "Forecasting…" : null);
  const questionFade = useQuestionFade({
    enabled: true,
    scrollRef: evtScrollRef,
    questionKey: displayTitle,
  });

  // After useTimelineFraming: every x is a day on the current framing.
  const tsdGeometry = useTsdGeometry({
    series,
    ppd,
    viewLeft,
    tsdH,
    todayX,
    active,
    mockToday,
    runState,
    branches,
    timelineToday,
    theme,
    themeRaw,
  });
  const timelineVis = useComputeVisibility({
    geom: timeline,
    active,
    railPanelSettled,
    railPanelWidth,
  });

  // The arrivals store stays here (C-23): its hook and the ring-clearing effect below read the mark
  // moments.
  const { fcMoments, fcMarkX, searchMoments, activeMarkIso, liveSentinelIso } = useMarkMoments({
    reforecastEntries,
    viewMomentIso,
    viewLeft,
    ppd,
    axisHoverX,
    hov,
  });
  // Unseen just-arrived fires (SSE push) render a pulse and a persistent "new" ring on their
  // timeline mark, keyed by moment.
  const unseenArrivals = useArrivals((s) => s.unseen);
  // currentResolutionEntry, scrubIsResolution and scrubResolutionSnapshot are the single source of
  // truth for which resolution attempt every surface renders.

  // Hover, click and scrub all clear the "new" ring through this one effect.
  useEffect(() => {
    if (activeMarkIso && isUnseen(unseenArrivals, activeMarkIso)) acknowledgeArrival(activeMarkIso);
  }, [activeMarkIso, unseenArrivals]);

  // A render-time branch below every hook: the pre-auth shells return before the contexts are built.
  const authShell = renderAuthShell({ authLoading, authEnabled, me, tos, setTos, themeRaw });
  if (authShell) return authShell;

  const reportCtx = {
    // The reforecast wiring's scrub state and handlers — among them whether the CURRENT carousel
    // position is a resolution entry (→ resolution slide) and that entry's inline resolution
    // snapshot.
    ...reforecastWiring,
    ...reportWiringCtx,
    // The pointer's report source and resolution, the Listen state, and the aggregate of the shift
    // verdict over this contract's fires (`shiftStabilitySummary`; null → the stability block
    // renders its (mandatory) empty state, i.e. nothing).
    ...reportPointerWiring,
    theme,
    reportListenEnabled,
    listenRowKey: reportRowKey,
    listenActive: !!reportListen.listen,
    onListen: reportListen.start,
    dynamicReportEnabled,
    forecastTracking,
    active,
    scrubMomentIso: viewMomentIso,
    scrubViewSnap: viewSnap,
    fcMoments,
    fcEntries: reforecastEntries,
    // chatboard-shift-verdict GOFF flag (from /api/me): master gate for the
    // "What changed since last forecast" box AND the "Forecast stability" aggregate.
    shiftVerdictEnabled,
    // chatboard-all-multiway (from /api/me): the report renders a binary forecast
    // as a two-bar distribution rather than the percent + verdict headline.
    allMultiwayEnabled,
    // chatboard-alternate-spaces GOFF flag (from /api/me): master gate for the
    // alternate-space section. Off, or a null `alternate_forecast` in the report, means the
    // section is absent.
    alternateSpacesEnabled,
    // Settings → debug "alternate space by default" (already ANDed with the flag):
    // the report's distribution IS the alternate, and the separate section is dropped.
    alternateSpaceDefault,
    setCitCard,
    effectiveToday,
    branches,
    // Gate + project for the report modal's cloud-console trace links.
    // gcpProject is from useAuthSession, NOT a field on `me` — see that
    // hook's comment. See also shouldShowTraceLinks.
    debugUI,
    gcpProject,
    // During the guided tour the Sayer chat is raised ABOVE the report (z450 > z400) so its
    // narration + Skip control stay usable, which can tuck the centered report under the
    // chat panel. The chat's width is reserved on the right, only while the chat is
    // actually raised over the report (tour + chat open), so the report centers CLEAR of
    // the chat; normal report views cover the chat as before.
    // In the navigation-rail layout the chat is on the LEFT, so the room is reserved there.
    reportBackdropStyle:
      tourActive && chatOpen ? { paddingLeft: RAIL_W + railPanelWidth + 16 } : undefined,
  };
  // Literals here, not inside SettingsHost: they read bindings declared after every settings hook.
  const settingsCtx = {
    settingsTitleClickRef,
    debugMenuAllowed: session.debugMenuAllowed,
    debugUIAllowed: session._debugUIAllowed,
    drawerHelpState,
    setDrawerHelpState,
    setProOpen,
    orchestrators,
    forecastEngine,
    setForecastEngine,
    mirabilisLanes,
    setMirabilisLanes,
    quickDeepSelectorAllowed,
    theme,
    setTheme,
    uiScale,
    uiScaleSetting,
    setUiScale,
    debugUI,
    setDebugUI,
    glowSpine,
    setGlowSpine,
    alternateSpacesEnabled,
    alternateSpaceDefault: alternateSpaceDefaultPref,
    setAlternateSpaceDefault: setAlternateSpaceDefaultPref,
    dynamicReportEnabled,
    glowControls,
    setGlowControls,
    compareMode,
    setCompareMode,
    compareEngine,
    setCompareEngine,
    triggerLlm,
    setTriggerLlm,
    compareTriggerLlm,
    setCompareTriggerLlm,
    proColors,
    debugMode,
    setDebugMode,
    mockToday,
    setMockToday,
    forecastTracking,
    debugResolutionOverride,
    setDebugResolutionOverride,
    debugPremiseOverride,
    setDebugPremiseOverride,
    debugForceGenerating,
    setDebugForceGenerating,
    clearDebugOverrides,
    debugForceAlertBadge,
    setDebugForceAlertBadge,
    responseCaptureOptIn,
    setResponseCaptureOptIn,
    preprocessVariantAvailable,
    preprocessVariantId,
    setPreprocessVariantId,
    preprocessVariants,
    preprocessVariantDeploymentDefault,
    preprocessVariantsLoading,
    tos,
    setTosPreview,
    voiceMode,
    autoRead,
    setAutoRead,
    llmModel,
    setLlmModel,
    llmPrompt,
    setLlmPrompt,
    llmLoading,
    setLlmLoading,
    llmResponse,
    setLlmResponse,
    gcpLoading,
    setGcpLoading,
    gcpResponse,
    setGcpResponse,
    setOrchestrators,
    active,
    dispatchRun,
    resetTips,
  };
  const accountCtx = {
    setAccountOpen,
    authEnabled,
    me,
    debugUI,
    gcpProject,
    persistenceEnabled,
    forecasts,
    pinned,
    lastObs,
    lastBugObs,
    logCounts: telemetryLogCounts,
    clearLogRings,
    drawerHelpState,
    setDrawerHelpState,
    openFeedback: () => {
      setAccountOpen(false);
      openBugReport();
    },
  };
  const frameCtx = {
    ...timeline,
    ...timelineFraming,
    ...timelineMouse,
    ...timelineVis,
    ...layoutShell,
    ...railPanel,
    ...forecastSelection,
    ...forecastSelectionActions,
    ...seriesCtx,
    ...tsdGeometry,
    ...submitState,
    ...submitWiring,
    ...submitEffects,
    ...chatComposer,
    ...chatWiring,
    ...session,
    ...trackingWiring,
    ...reforecastWiring,
    ...trackingSettingsWiring,
    ...reportWiringCtx,
    ...settingsFrameCtx,
    ...bootCtx,
    ...branchWiring,
    ...shareWiring,
    ...onboardingCtx,
    CALIBRATION_MODEL_ID,
    active,
    activeMarkIso,
    animPhase,
    asyncMoments,
    branchAnim,
    citCard,
    debugForceGenerating,
    debugResolutionOverride,
    fcMarkX,
    forecastPointerCount,
    forecastTracking,
    hov,
    hovEvtRef,
    hovPos,
    hoveredBin,
    hoveredBranch: hoveredBranch ?? timelineFraming.branchLinkGlowId,
    hoveredBranchTrigger,
    longPressRef,
    onTsdHandleMD,
    questionFade,
    redBlueTeam,
    removeBranch,
    searchMoments,
    setHov,
    setHovPos,
    setHoveredBin,
    setHoveredBranch,
    setHoveredBranchTrigger,
    toggleBranchCollapsed,
    tsdH,
    unseenArrivals,
    comparison,
    contractBlink,
    debugForceAlertBadge,
    dispatchRun,
    forecasts,
    forecastsById,
    getRunState,
    handleSelectRef,
    liveSentinelIso,
    mkDeleteDeps,
    pushSayerMsg,
    setAnimPhase,
    setBranchAnim,
    sharedForecasts,
    slideCache,
    startNewQuestion,
    titlePreprocessing,
    tsDrawerOpen,
    addSeries,
    openBugReport,
    removeSeries,
    series,
    setTsDrawerOpen,
    setTsdH,
    tsdAnimating,
    verifySeries,
    bugOpen,
    handleSelectTier,
    chatJournal,
    chatThinking,
    runState,
    accessNotice,
    customEvtPopover,
    customEvtPopoverRunId,
    fcMoments,
    revokeMyAccess,
    reportListen,
    debugPanelOpen,
    setDebugPanelOpen,
  };
  return (
    // CSS zoom on the root, with viewport units divided by the same factor to compensate so
    // nothing overflows. The user's "Display size" preset multiplies the base
    // (rootZoomFor(uiScale)). Pointer→timeline math uses clientToLocalX, which derives the zoom
    // factor dynamically, so it stays correct for every theme and every display size.
    <div
      style={{
        width: `calc(100vw / ${uiZoom})`,
        height: `calc(100vh / ${uiZoom})`,
        zoom: uiZoom,
        background: C.bg,
        color: C.white,
        fontFamily: `"AvenirPercent",${C.font || '"IBM Plex Mono",monospace'}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      onMouseMove={onMM}
      onMouseUp={onMU}
      onMouseLeave={onML}
    >
      <GlobalStyles />
      <Toaster />
      <StaleBundleBanner />
      {/* A phone gets the read-only layout in place of the desktop shell; the
         report and the overlays below render for both. */}
      <AppFrame
        ctx={frameCtx}
        shareDialog={<ShareHost ctx={frameCtx} />}
        hero={<SubmitBar ctx={frameCtx} />}
        runStatus={<RunStatusSlot ctx={frameCtx} />}
        viewMomentBlock={<ViewMomentBlock ctx={frameCtx} />}
        tsdChart={<TsdChartSlot ctx={frameCtx} />}
        trackingMarks={<TrackingBlocks ctx={frameCtx} />}
        trackingSettingsOverlay={<TrackingSettingsOverlay ctx={frameCtx} />}
        chatOverlay={<ChatPanel ctx={frameCtx} />}
        signalBar={<SignalBar ctx={frameCtx} />}
        overlays={
          <>
            {selRect && selRect.width > 5 && (
              <div
                style={{
                  position: "fixed",
                  // A `fixed` child of the zoom:uiZoom root paints at coordinate * uiZoom,
                  // while a mouse event's clientX is unscaled — so client px must be divided
                  // by uiZoom to land where the cursor is. `selRect` itself stays in client px
                  // because the zoom math on release reads it back through clientToLocalX.
                  left: selRect.left / uiZoom,
                  top: selRect.top / uiZoom,
                  width: selRect.width / uiZoom,
                  height: selRect.height / uiZoom,
                  border: "1px dashed #88999988",
                  background: "rgba(100,120,140,0.08)",
                  pointerEvents: "none",
                  zIndex: 100,
                }}
              />
            )}

            <DebugHost ctx={frameCtx} />

            <SignalLibraryOverlay ctx={frameCtx} />

            <TipsHost ctx={frameCtx} />
            <SettingsHost ctx={frameCtx} settings={settingsCtx} account={accountCtx} />
          </>
        }
        modals={
          <>
            <SendCopyHost ctx={frameCtx} />

            <PreprocessHost ctx={frameCtx} />
          </>
        }
      />

      <ReportHost ctx={frameCtx} report={reportCtx} />
      <BugReportHost ctx={frameCtx} bugReport={bugReportCtx} />
      {selEvt &&
        (() => {
          const { root: selEvtRoot, clickedBranchId } = resolveClickedRoot(selEvt._rowId, {
            active,
            compareRoots,
            runState,
          });
          return (
            <EventModal
              ev={selEvt}
              today={effectiveToday.getTime()}
              onClose={() => setSelEvt(null)}
              // Prefetches the derive path's report read while the cards are on screen, so an
              // OCCURS / DOESN'T OCCUR click does not pay for it.
              onOpen={prefetchDerivedBranch}
              onBranch={(ev, t, v, cancelledRef) => {
                handleBranch(ev, t, v, cancelledRef);
              }}
              // The owning root's branch RUN tree. EventModal reads only b.forkEvent (1:1),
              // so run objects pass straight through.
              branches={selectBranchRuns(runState, selEvtRoot?.id)}
              // The RUN whose spine the clicked event sits on. A branch forked off THIS event
              // carries parentRunId === this run. Branch-row event → the branch's own runId;
              // main/compare spine → the resolved root's runId. EventModal scopes
              // hasOccurs/hasNotOccurs on this so a branch trigger can't inherit OCCUR/DOESN'T
              // suppression from a main-spine event that shares its (per-spine, non-unique) id.
              sourceRunId={clickedBranchId ?? selEvtRoot?.id}
              // The engine a branch off this event will run on: the run it will PARENT off (the
              // clicked branch run on a branch row, else the root), with the picker only as
              // fallback; the same resolution useBranchForecast gates on.
            />
          );
        })()}

      {/* Outside the desktop-only AppFrame slots: the ToS preview serves the phone layout too. */}
      <TosPreviewHost ctx={frameCtx} />

      {/* "No longer shared with you." */}
      <AccessNoticeStrip ctx={frameCtx} />
      <LimitPopup ctx={frameCtx} />

      <BranchControls ctx={frameCtx} />
      <DeleteConfirmModal
        deleteConfirm={deleteConfirm}
        setDeleteConfirm={setDeleteConfirm}
        mkDeleteDeps={mkDeleteDeps}
        forecastStacking={forecastStacking}
        removeFromForecastOrder={removeFromForecastOrder}
      />

      <CustomEventPopover ctx={frameCtx} />

      {/* `forecastTracking` is a DISPLAY gate, NOT a stop button. Flipping it off decides
         what THIS CLIENT shows and fetches — the tracking surface, the resolver poll, the
         report-pointer reads. It does not stop forecasts already on a cadence and does not
         stop alert emails (the notifier gates on `chatboard-notify-email`). The forecast list
         renders either way. See `chatboard-forecast-tracking` in cloud/feature-flags before
         pulling it in an incident. */}
      {forecastTracking && null}
    </div>
  );
}

export default function App() {
  // The one route this SPA has. `/copy/:id` is a forecast COPY link — a URL someone
  // else sent the visitor — so it renders INSTEAD OF `AppInner`, not inside it. That
  // placement is load-bearing, not stylistic: `AppInner` bounces an unauthenticated
  // visitor to Zitadel during render (the `authEnabled && !me` shell of
  // features/session/AuthShells.jsx's `renderAuthShell`, called above), which
  // discards the URL fragment the link's secret rides in before any effect could
  // park it. Rendering outside `AppInner` lets the redeem POST's own 401 drive the
  // login round trip instead, after the secret is safely stashed. See
  // components/RedeemCopyRoute.jsx's header before moving this.
  const copyId = matchCopyRoute(typeof window === "undefined" ? null : window.location.pathname);
  // Dev-only routes are gated on `import.meta.env.DEV`, which is statically FALSE in every `vite
  // build` (production and the debug build alike), so the routes and their lazy imports are
  // dead-code-eliminated and cannot ship.
  // Dev-only PREVIEW of the link landing page (`/dev/link-landing`) — the screen a recipient
  // without an account sees. Previewable because the real path needs a signed-OUT visitor holding
  // a link-shared URL, which local dev cannot produce: the IAP dev bypass signs everyone in.
  //
  // The sample props are FIXED, with no query params: a `?sender=` read would add a
  // `window.location.search` read in this file and regress the ratchet's `strayLocationSearch`
  // counter (its readers are the `STRAY_LOCATION_SEARCH_DECLARED` table in
  // `app/scripts/ratchet.mjs`; URL reading belongs in the view-state layer, not ad hoc in
  // App.jsx).
  if (import.meta.env.DEV && typeof window !== "undefined") {
    if (window.location.pathname.startsWith("/dev/link-landing")) {
      return (
        <ErrorBoundary>
          <LinkLandingPage senderName="Chuck Hoover" forecastUrl={window.location.origin} />
        </ErrorBoundary>
      );
    }
  }
  if (import.meta.env.DEV && CardStatesPreview && typeof window !== "undefined") {
    if (window.location.pathname.startsWith("/dev/card-states")) {
      return (
        <ErrorBoundary>
          <Suspense fallback={null}>
            <CardStatesPreview />
          </Suspense>
        </ErrorBoundary>
      );
    }
  }
  if (import.meta.env.DEV && SettingsStatesPreview && typeof window !== "undefined") {
    if (window.location.pathname.startsWith("/dev/settings-states")) {
      return (
        <ErrorBoundary>
          <Suspense fallback={null}>
            <SettingsStatesPreview />
          </Suspense>
        </ErrorBoundary>
      );
    }
  }
  if (import.meta.env.DEV && DeepRunPreview && typeof window !== "undefined") {
    if (window.location.pathname.startsWith("/dev/deep-run")) {
      return (
        <ErrorBoundary>
          <Suspense fallback={null}>
            <DeepRunPreview />
          </Suspense>
        </ErrorBoundary>
      );
    }
  }
  if (import.meta.env.DEV && ShareDialogPreview && typeof window !== "undefined") {
    if (window.location.pathname.startsWith("/dev/share-dialog")) {
      return (
        <ErrorBoundary>
          <Suspense fallback={null}>
            <ShareDialogPreview />
          </Suspense>
        </ErrorBoundary>
      );
    }
  }
  if (import.meta.env.DEV && ResolutionMatrix && typeof window !== "undefined") {
    if (window.location.pathname.startsWith("/dev/resolution-matrix")) {
      return (
        <ErrorBoundary>
          <Suspense fallback={null}>
            <ResolutionMatrix />
          </Suspense>
        </ErrorBoundary>
      );
    }
  }
  return (
    <ErrorBoundary>
      {copyId ? (
        <RedeemCopyRoute copyId={copyId} />
      ) : (
        <SessionProvider setAuthEnabledFlag={setAuthEnabledFlag}>
          <AppInner />
        </SessionProvider>
      )}
    </ErrorBoundary>
  );
}
